import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, finalize, retry } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { SpinnerService } from '../spinner';
import { ErrorHandlingService } from '../error-handling/error-handling.service';
import { HttpHeaderConstants } from '../http-header.constant';
import {
    ContentAssetElement,
    ContentFolderDependency,
    ContentLibraryElement,
    FolderElement,
} from '../../models/tfs/content-folder-element.model';
import { PagedResponse } from '../../models/appcloud/paged-response.model';
import { ContentAssetSearch } from '../../models/tfs/content-asset-search.model';

@Injectable({
    providedIn: 'root',
})
export class ContentService {
    readonly #http = inject(HttpClient);
    readonly #spinnerService = inject(SpinnerService);
    readonly #errorHandlingService = inject(ErrorHandlingService);

    private BASE_URL: string = environment.publicApiHost + '/appcloud/content-library';
    private CONTENT_FOLDERS_URL: string = this.BASE_URL + '/folders';
    private CONTENT_ASSETS_URL: string = this.BASE_URL + '/assets';
    private CONTENT_LIBRARY_TABLES: string = this.BASE_URL + '/tables';

    getRootLevelElements(eloquaInstallId: number, additionalHeaders: any = {}): Observable<ContentLibraryElement[]> {
        this.#spinnerService.addProcess('ContentService.getParentFolders', 'Looking for folders...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };

        return this.#http.get<ContentLibraryElement[]>(this.CONTENT_FOLDERS_URL, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve dynamic content folders. Please try again later.'
                );
                return of<ContentLibraryElement[]>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.getParentFolders'))
        );
    }

    getChildFolders(
        eloquaInstallId: number,
        folderId: string,
        additionalHeaders: any = {}
    ): Observable<FolderElement[]> {
        this.#spinnerService.addProcess('ContentService.getChildFolders.' + folderId, 'Looking for folders...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http
            .get<FolderElement[]>(this.buildChildElementsUrl(folderId, 'children'), customHttpOptions)
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while trying to retrieve dynamic content folders. Please try again later.'
                    );
                    return of<FolderElement[]>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ContentService.getChildFolders.' + folderId))
            );
    }

    getChildAssets(
        eloquaInstallId: number,
        folderId: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<ContentAssetElement[]> {
        this.#spinnerService.addProcess('ContentService.getChildAssets.' + folderId, 'Looking for assets...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('eloquainstallId', JSON.stringify(eloquaInstallId))
                .set('installDataId', installDataId),
        };
        return this.#http
            .get<ContentAssetElement[]>(this.buildChildElementsUrl(folderId, 'assets'), customHttpOptions)
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while trying to retrieve dynamic content assets. Please try again later.'
                    );
                    return of<ContentAssetElement[]>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ContentService.getChildAssets.' + folderId))
            );
    }

    searchAssets(
        eloquaInstallId: number,
        installDataId: string,
        searchObject: ContentAssetSearch,
        additionalHeaders: any = {}
    ): Observable<PagedResponse<ContentLibraryElement>> {
        this.#spinnerService.addProcess('ContentService.searchAssets', 'Looking for assets...');
        const httpOptions = {
            headers: new HttpHeaders({
                ...HttpHeaderConstants.defaultHeaders,
                ...additionalHeaders,
            }),
            params: new HttpParams().set('installDataId', installDataId),
        };
        httpOptions.params = this.appendOptionalParams(httpOptions.params, searchObject);
        return this.#http.get(this.CONTENT_ASSETS_URL, httpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to retrieve dynamic content assets. Please try again later.'
                );
                return of<PagedResponse<ContentLibraryElement>>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.searchAssets'))
        );
    }

    createFolder(
        eloquaInstallId: number,
        folder: FolderElement,
        additionalHeaders: any = {}
    ): Observable<FolderElement> {
        this.#spinnerService.addProcess('ContentService.createFolder', 'Creating your folder...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http.post<FolderElement>(this.CONTENT_FOLDERS_URL, folder, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to create a new folder. Please try again later.'
                );
                return of<FolderElement>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.createFolder'))
        );
    }

    editFolder(eloquaInstallId: number, folder: FolderElement, additionalHeaders: any = {}): Observable<FolderElement> {
        this.#spinnerService.addProcess('ContentService.editFolder', 'Updating your folder...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http
            .put<FolderElement>(this.CONTENT_FOLDERS_URL + '/' + folder.id, folder, customHttpOptions)
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while trying to edit your folder. Please try again later.'
                    );
                    return of<FolderElement>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ContentService.editFolder'))
            );
    }

    deleteFolder(eloquaInstallId: number, folderId: number, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('ContentService.deleteFolder', 'Deleting your folder...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http.delete(this.CONTENT_FOLDERS_URL + '/' + folderId, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to delete your folder. Please try again later.'
                );
                return of<any>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.deleteFolder'))
        );
    }

    editAsset(
        eloquaInstallId: number,
        folderId: string,
        asset: ContentAssetElement,
        additionalHeaders: any = {}
    ): Observable<ContentAssetElement> {
        this.#spinnerService.addProcess('ContentService.editAsset', 'Editing your asset...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http
            .put<ContentAssetElement>(this.buildUpdateAssetUrl(folderId, asset.id), asset, customHttpOptions)
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while trying to edit your asset. Please try again later.'
                    );
                    return of<ContentAssetElement>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ContentService.editAsset'))
            );
    }

    createAsset(
        eloquaInstallId: number,
        folderId: string,
        asset: ContentAssetElement,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<ContentAssetElement> {
        this.#spinnerService.addProcess('ContentService.createAsset', 'Creating your asset...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('eloquainstallId', JSON.stringify(eloquaInstallId))
                .set('installDataId', installDataId),
        };
        return this.#http.post<ContentAssetElement>(this.buildCreateAssetUrl(folderId), asset, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to create your asset. Please try again later.'
                );
                return of<ContentAssetElement>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.createAsset'))
        );
    }

    deleteAsset(eloquaInstallId: number, assetId: number, additionalHeaders: any = {}) {
        this.#spinnerService.addProcess('ContentService.deleteAsset', 'Deleting your asset...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http.delete(this.CONTENT_ASSETS_URL + '/' + assetId, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to delete your asset. Please try again later.'
                );
                return of<any>();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.deleteAsset'))
        );
    }

    getAllDependencies(
        eloquaInstallId: number,
        folderId: string,
        installDataId: string,
        additionalHeaders: any = {}
    ): Observable<ContentFolderDependency> {
        this.#spinnerService.addProcess('ContentService.getAllDependencies', 'Getting all dependencies...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams()
                .set('eloquainstallId', JSON.stringify(eloquaInstallId))
                .set('installDataId', installDataId),
        };
        return this.#http
            .get<ContentFolderDependency>(this.buildGetAllDependenciesUrl(folderId), customHttpOptions)
            .pipe(
                retry(0),
                catchError(error => {
                    this.#errorHandlingService.handleHttpError(
                        error,
                        false,
                        'An error occurred while trying to retrieve all dependent folders and assets. Please try again later.'
                    );
                    return of<ContentFolderDependency>();
                }),
                finalize(() => this.#spinnerService.completeProcess('ContentService.getAllDependencies'))
            );
    }

    createTables(eloquaInstallId: number, additionalHeaders: any = {}): Observable<any> {
        this.#spinnerService.addProcess('ContentService.createTables', 'Creating your content library tables...');
        const customHttpOptions = {
            headers: new HttpHeaders({ ...HttpHeaderConstants.defaultHeaders, ...additionalHeaders }),
            params: new HttpParams().set('eloquainstallId', JSON.stringify(eloquaInstallId)),
        };
        return this.#http.post<any>(this.CONTENT_LIBRARY_TABLES, null, customHttpOptions).pipe(
            retry(0),
            catchError(error => {
                this.#errorHandlingService.handleHttpError(
                    error,
                    false,
                    'An error occurred while trying to create your content library tables. Please try again later.'
                );
                return of();
            }),
            finalize(() => this.#spinnerService.completeProcess('ContentService.createTables'))
        );
    }

    private buildChildElementsUrl(folderId: string, type: string): string {
        return this.CONTENT_FOLDERS_URL + '/' + folderId + '/' + type;
    }

    private buildUpdateAssetUrl(folderId: string, assetId: number): string {
        return this.CONTENT_FOLDERS_URL + '/' + folderId + '/assets/' + assetId;
    }

    private buildCreateAssetUrl(folderId: string): string {
        return this.CONTENT_FOLDERS_URL + '/' + folderId + '/assets';
    }

    private buildGetAllDependenciesUrl(folderId: string): string {
        return this.CONTENT_FOLDERS_URL + '/' + folderId + '/dependencies';
    }

    private appendOptionalParams(httpParams: HttpParams, searchObj: ContentAssetSearch): HttpParams {
        const entries = Object.entries(searchObj);
        for (const [name, value] of entries) {
            if (value) {
                if (name === 'page' || name === 'pageSize') {
                    httpParams = httpParams.append(name, value.toString());
                } else {
                    httpParams = httpParams.append(name, value);
                }
            }
        }
        return httpParams;
    }
}
